import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { Button, Tooltip, Box, Typography } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import AddIcon from "@material-ui/icons/Add";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getReviews, deleteReview, updateReview } from "../../../../api/review";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useHistory } from "react-router-dom";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";

const buttonsStyle = {
  maxWidth: "30px",
  maxHeight: "30px",
  minWidth: "30px",
  minHeight: "30px",
  marginLeft: "4px",
};

function SortableReviewCard({ item, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const textPreview =
    (item.text || "").length > 80
      ? (item.text || "").slice(0, 80) + "..."
      : item.text || "";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="d-flex align-items-start p-3 mb-2 rounded border bg-white"
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          cursor: "grab",
          padding: "4px",
          marginRight: "8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </div>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {item.author || "—"}
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          {item.date || "—"}
        </Typography>
        {item.title && (
          <Typography variant="caption" display="block" style={{ fontStyle: "italic" }}>
            {item.title}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="textSecondary"
          style={{
            marginTop: 4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {textPreview || "—"}
        </Typography>
      </div>
      <div className="d-flex align-items-center">
        <Tooltip title="Editar">
          <Button
            style={buttonsStyle}
            size="small"
            onClick={() => onEdit(item.id)}
          >
            <EditIcon fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Eliminar">
          <Button
            style={buttonsStyle}
            size="small"
            onClick={() => onDelete(item.id)}
          >
            <DeleteIcon fontSize="small" />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

function ReviewSection({ title, source, items, onReorder, onEdit, onDelete, onAdd }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const next = arrayMove(items, oldIndex, newIndex);
      onReorder(source, next);
    },
    [items, source, onReorder]
  );

  const ids = items.map((i) => i.id);

  return (
    <Card className="mb-4">
      <CardHeader
        title={title}
        toolbar={
          <CardHeaderToolbar>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onAdd(source)}
            >
              Añadir {title}
            </Button>
          </CardHeaderToolbar>
        }
      />
      <CardBody>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {items.length === 0 ? (
              <Typography color="textSecondary" style={{ fontStyle: "italic" }}>
                Sin reviews. Añade una con el botón anterior.
              </Typography>
            ) : (
              items.map((item) => (
                <SortableReviewCard
                  key={item.id}
                  item={item}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))
            )}
          </SortableContext>
        </DndContext>
      </CardBody>
    </Card>
  );
}

function getData(reviews) {
  return (reviews || []).map((r) => ({
    id: r._id,
    source: r.source || "",
    stars: 5,
    text: r.text || "",
    author: r.author || "",
    date: r.date || "",
    title: r.title || "",
    order: r.order ?? 0,
  }));
}

function toUpdatePayload(item, order) {
  return {
    source: item.source,
    stars: 5,
    text: item.text,
    author: item.author,
    date: item.date,
    title: item.title || "",
    order,
  };
}

function splitAndSort(reviews) {
  const tp = (reviews || []).filter((r) => r.source === "trustpilot");
  const ch = (reviews || []).filter((r) => r.source === "chrono24");
  const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);
  tp.sort(byOrder);
  ch.sort(byOrder);
  return { trustpilot: tp, chrono24: ch };
}

export default function ReviewsPage() {
  const [trustpilot, setTrustpilot] = useState([]);
  const [chrono24, setChrono24] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const history = useHistory();

  const fetchReviews = useCallback(() => {
    setLoading(true);
    getReviews()
      .then((res) => {
        if (res.status === 200) {
          const data = getData(res.data);
          const { trustpilot: tp, chrono24: ch } = splitAndSort(data);
          setTrustpilot(tp);
          setChrono24(ch);
        }
        setLoading(false);
      })
      .catch((err) => {
        alertError({ error: err, customMessage: "No se pudieron cargar las reviews." });
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleReorder = useCallback(
    async (source, nextItems) => {
      if (source === "trustpilot") setTrustpilot(nextItems);
      else setChrono24(nextItems);
      const promises = nextItems.map((item, index) =>
        updateReview(item.id, toUpdatePayload(item, index))
      );
      try {
        await Promise.all(promises);
        alertSuccess({ title: "Orden actualizado", customMessage: "Se guardó el nuevo orden." });
      } catch (err) {
        alertError({ error: err, customMessage: "Error al guardar el orden." });
        fetchReviews();
      }
    },
    [fetchReviews]
  );

  const handleEdit = (id) => history.push("/edit-review/" + id);
  const handleDelete = (id) => {
    setReviewToDelete(id);
    setOpenConfirmDialog(true);
  };
  const handleAdd = (source) => history.push("/edit-review?source=" + source);

  const handleConfirmDelete = () => {
    if (!reviewToDelete) return;
    deleteReview(reviewToDelete)
      .then((res) => {
        if (res.status === 200 || res.status === 204) {
          alertSuccess({
            title: "Eliminado",
            customMessage: "Review eliminada correctamente.",
          });
          fetchReviews();
          setOpenConfirmDialog(false);
          setReviewToDelete(null);
        }
      })
      .catch((err) => {
        alertError({ error: err, customMessage: "No se pudo eliminar la review." });
      });
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <p className="text-muted">Cargando reviews…</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Box mb={2}>
        <Typography variant="h6" gutterBottom>
          Reviews (TrustReviews / Home)
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Arrastra las cajitas para cambiar el orden. TrustPilot y Chrono24 se muestran por separado en la web.
        </Typography>
      </Box>

      <ReviewSection
        title="TrustPilot"
        source="trustpilot"
        items={trustpilot}
        onReorder={handleReorder}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
      />

      <ReviewSection
        title="Chrono24"
        source="chrono24"
        items={chrono24}
        onReorder={handleReorder}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
      />

      <ConfirmDialog
        title="¿Eliminar esta review?"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
